// Card de speaker no identificado (Fase 41).
//
// Muestra el label del speaker, cuántas veces habla, primera intervención (▶),
// y permite buscar en el directorio o crear una persona nueva para asignarla.
// Una vez asignada, muestra la persona y permite cambiar la asignación.

import { useState, useMemo } from "react";
import styled from "styled-components";
import type { DirectoryPerson, SpeakerIdentification } from "../../platform/api";
import { addDirectoryPerson } from "../../platform/api";
import type { SpeakerAssignment } from "../../state/atoms";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function formatInterventions(n: number): string {
  return n === 1 ? "1 intervención" : `${n} intervenciones`;
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const CardWrapper = styled.div<{ $assigned: boolean }>`
  padding: 16px;
  border: 1px solid ${({ theme, $assigned }) =>
    $assigned ? theme.color.success : theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $assigned }) =>
    $assigned ? theme.color.successMuted : theme.color.surface};
  margin-bottom: 12px;
  opacity: ${({ $assigned }) => ($assigned ? 0.75 : 1)};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const Timestamp = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.accent};
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const SpeakerLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.md};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const InterventionCount = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  margin-left: auto;
`;

const SectionLabel = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textDim};
  margin-bottom: 4px;
`;

const SearchRow = styled.div`
  position: relative;
  margin-top: 8px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.font.size.sm};
  font-family: ${({ theme }) => theme.font.family};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.accent};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  box-shadow: ${({ theme }) => theme.shadow.md};
  z-index: 10;
`;

const DropdownItem = styled.button`
  display: block;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: none;
  text-align: left;
  font-size: ${({ theme }) => theme.font.size.sm};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.accentMuted};
  }
`;

const DropdownItemName = styled.span`
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const DropdownItemMeta = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin-left: 8px;
`;

const DropdownAddNew = styled(DropdownItem)`
  border-top: 1px solid ${({ theme }) => theme.color.border};
  color: ${({ theme }) => theme.color.accent};
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const FormWrapper = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.accent};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.accentMuted};
`;

const FormTitle = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  margin-bottom: 8px;
`;

const FormField = styled.div`
  margin-bottom: 6px;
`;

const FormLabel = styled.label`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
  display: block;
  margin-bottom: 2px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.font.size.sm};
  font-family: ${({ theme }) => theme.font.family};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.accent};
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

const ActionButton = styled.button<{ $variant: "primary" | "cancel" }>`
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid;
  font-size: ${({ theme }) => theme.font.size.sm};
  cursor: pointer;
  background: ${({ theme, $variant }) =>
    $variant === "primary" ? theme.color.accentMuted : theme.color.surface};
  color: ${({ theme, $variant }) =>
    $variant === "primary" ? theme.color.accent : theme.color.textMuted};
  border-color: ${({ theme, $variant }) =>
    $variant === "primary" ? theme.color.accent : theme.color.border};
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AssignedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const AssignedLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.success};
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const ChangeButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.accent};
  font-size: ${({ theme }) => theme.font.size.sm};
  cursor: pointer;
  padding: 2px 4px;
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorText = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.error};
  margin-top: 4px;
  display: block;
`;

const PlayButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.color.accent : theme.color.border};
  background: ${({ theme, $active }) =>
    $active ? theme.color.accentMuted : theme.color.surface};
  color: ${({ theme, $active }) =>
    $active ? theme.color.accent : theme.color.textMuted};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  flex-shrink: 0;
  &:hover {
    border-color: ${({ theme }) => theme.color.accent};
    color: ${({ theme }) => theme.color.accent};
  }
`;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  speaker: SpeakerIdentification;
  segmentCount: number;
  firstTimestamp: number | null;
  directory: DirectoryPerson[];
  assignment?: SpeakerAssignment;
  onAssign: (srtLabel: string, assignment: SpeakerAssignment | null) => void;
  onDirectoryRefresh: () => Promise<void>;
  playingId?: string | null;
  onPlaySample?: (id: string, startTime: number) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UnidentifiedSpeakerCard({
  speaker,
  segmentCount,
  firstTimestamp,
  directory,
  assignment,
  onAssign,
  onDirectoryRefresh,
  playingId,
  onPlaySample,
}: Props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Form fields
  const [formNames, setFormNames] = useState("");
  const [formCargo, setFormCargo] = useState("");
  const [formEmpresa, setFormEmpresa] = useState("");
  const [formEmail, setFormEmail] = useState("");

  const isAssigned = assignment != null;
  const playId = `speaker-${speaker.srtLabel}`;

  const filteredDirectory = useMemo(() => {
    if (!searchQuery || searchQuery.length < 1) return directory.slice(0, 8);
    const lower = searchQuery.toLowerCase();
    return directory
      .filter((p) =>
        p.names.some((n) => n.toLowerCase().includes(lower)),
      )
      .slice(0, 8);
  }, [searchQuery, directory]);

  const handleSelectPerson = (person: DirectoryPerson) => {
    onAssign(speaker.srtLabel, {
      personId: person.id,
      personName: person.names[0],
    });
    setSearchQuery("");
    setShowDropdown(false);
    setShowCreateForm(false);
  };

  const handleStartCreate = () => {
    setShowCreateForm(true);
    setShowDropdown(false);
    setCreateError(null);
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setFormNames("");
    setFormCargo("");
    setFormEmpresa("");
    setFormEmail("");
    setCreateError(null);
  };

  const handleCreateAndAssign = async () => {
    const namesList = formNames
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);

    if (namesList.length === 0) {
      setCreateError("El nombre es obligatorio.");
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      const newPerson = await addDirectoryPerson({
        names: namesList,
        cargo: formCargo.trim() || undefined,
        empresa: formEmpresa.trim() || undefined,
        email: formEmail.trim() || undefined,
      });

      onAssign(speaker.srtLabel, {
        personId: newPerson.id,
        personName: newPerson.names[0],
      });

      setShowCreateForm(false);
      setFormNames("");
      setFormCargo("");
      setFormEmpresa("");
      setFormEmail("");

      // Refresh directory in parent so the new person is available for others
      await onDirectoryRefresh();
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Error al crear la persona.",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleChange = () => {
    onAssign(speaker.srtLabel, null);
  };

  return (
    <CardWrapper $assigned={isAssigned}>
      <CardHeader>
        {onPlaySample && firstTimestamp != null && (
          <PlayButton
            $active={playingId === playId}
            onClick={(e) => {
              e.stopPropagation();
              onPlaySample(playId, firstTimestamp);
            }}
            title="Escuchar 5s"
          >
            {playingId === playId ? "⏸" : "▶"}
          </PlayButton>
        )}
        <Timestamp>
          {firstTimestamp != null ? formatTimestamp(firstTimestamp) : "--:--:--"}
        </Timestamp>
        <SpeakerLabel>{speaker.srtLabel}</SpeakerLabel>
        <InterventionCount>{formatInterventions(segmentCount)}</InterventionCount>
      </CardHeader>

      {isAssigned ? (
        <AssignedRow>
          <AssignedLabel>
            ✅ Asignado: {assignment.personName}
          </AssignedLabel>
          <ChangeButton onClick={handleChange}>Cambiar</ChangeButton>
        </AssignedRow>
      ) : !showCreateForm ? (
        <>
          <SectionLabel>Asignar persona del directorio:</SectionLabel>
          <SearchRow>
            <SearchInput
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="Buscar en directorio..."
            />
            {showDropdown && (
              <Dropdown>
                {filteredDirectory.map((person) => (
                  <DropdownItem
                    key={person.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectPerson(person);
                    }}
                  >
                    <DropdownItemName>
                      {person.names[0]}
                    </DropdownItemName>
                    {(person.cargo || person.empresa) && (
                      <DropdownItemMeta>
                        {[person.cargo, person.empresa]
                          .filter(Boolean)
                          .join(", ")}
                      </DropdownItemMeta>
                    )}
                  </DropdownItem>
                ))}
                <DropdownAddNew
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleStartCreate();
                  }}
                >
                  + Agregar al directorio
                </DropdownAddNew>
              </Dropdown>
            )}
          </SearchRow>
        </>
      ) : (
        <FormWrapper>
          <FormTitle>Nueva persona</FormTitle>
          <FormField>
            <FormLabel>Nombre(s)*</FormLabel>
            <FormInput
              value={formNames}
              onChange={(e) => setFormNames(e.target.value)}
              placeholder="Ej: Juan Pérez"
              onKeyDown={(e) => {
                if (e.key === "Escape") handleCancelCreate();
              }}
              autoFocus
            />
          </FormField>
          <FormField>
            <FormLabel>Cargo</FormLabel>
            <FormInput
              value={formCargo}
              onChange={(e) => setFormCargo(e.target.value)}
              placeholder="Ej: Director"
            />
          </FormField>
          <FormField>
            <FormLabel>Empresa</FormLabel>
            <FormInput
              value={formEmpresa}
              onChange={(e) => setFormEmpresa(e.target.value)}
              placeholder="Ej: Accenture"
            />
          </FormField>
          <FormField>
            <FormLabel>Email</FormLabel>
            <FormInput
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              placeholder="Ej: juan@ejemplo.com"
              type="email"
            />
          </FormField>
          {createError && <ErrorText>{createError}</ErrorText>}
          <FormActions>
            <ActionButton
              $variant="primary"
              onClick={handleCreateAndAssign}
              disabled={creating}
            >
              {creating ? "Creando..." : "✓ Crear y asignar"}
            </ActionButton>
            <ActionButton
              $variant="cancel"
              onClick={handleCancelCreate}
              disabled={creating}
            >
              Cancelar
            </ActionButton>
          </FormActions>
        </FormWrapper>
      )}
    </CardWrapper>
  );
}
